import { observable, reaction, makeObservable, action } from "mobx";
import { createContext, useContext } from "react";
import TaskPackage from "./TaskPackage";
import {
  rebaselineProject,
  projectPrebaselines,
  checkHash,
  getProjectInfo,
} from "../operations/ProjectOperations";
import NBaseline from "./NBaseline";
import PreBaseline from "../model/PreBaseline";

export default class ProjectStore {
  id = "";
  name = "";
  folderName = "";
  ownerName = "";
  baselines = [];
  preBaselines = [];
  currentHash = "";
  rootTaskPackage = {};
  rootTaskPackageId = "";
  dataPeriod = 1;
  isArchive = false;
  periodToDateLookup = {};

  constructor(
    id,
    name,
    folderName,
    ownerName,
    baselines,
    preBaselines,
    currentHash,
    rootTaskPackage
  ) {
    this.id = id;
    this.name = name;
    this.folderName = folderName;
    this.ownerName = ownerName;
    this.baselines = baselines === null ? [] : baselines;
    this.currentHash = currentHash;
    this.preBaselines = preBaselines === null ? [] : preBaselines;
    this.rootTaskPackage = rootTaskPackage === null ? {} : rootTaskPackage;

    makeObservable(this, {
      name: observable,
      rootTaskPackage: observable,
      baselines: observable,
      preBaselines: observable,
      dataPeriod: observable,
    })
  }

  static EmptyProjectStore(name, ownerName) {
    return new ProjectStore(
      "", //new ObjectId().toHexString(),
      name,
      "template",
      ownerName,
      [],
      [],
      0,
      TaskPackage.RootTaskPackage(this)
    );
  }

  updateStore = action((project) => {
    if (project) {
      this.id = project.id;
      this.name = project.name;
      this.folderName = project.folderName;
      this.ownerName = project.ownerName;
      this.baselines = project.baselines === null ? [] : project.baselines.map((baseline) => NBaseline.fromJS(baseline));
      this.currentHash = project.currentHash;
      this.preBaselines = project.preBaselines === null ? [] : project.preBaselines;
      this.rootTaskPackage = project.rootTaskPackage === null ? {} : TaskPackage.fromJS(this, project.rootTaskPackage, "");
      this.rootTaskPackageId = project.rootTaskPackageId;
      this.isArchive = project.isArchive;
      this.dataPeriod = project.dataPeriod;
      this.periodToDateLookup = project.periodToDateLookup;
    }
  });

  saveProjectDisposer = reaction(
    () => {
      return this.toJS();
    },
    (data) => {
      localStorage.setItem("sma_eva_state_mobx", JSON.stringify(data));
    },
    { delay: 5000 }
  );

  updateRankingsDisposer = reaction(
    () => {
      return {
        //slight overkill to establish observation on every property (e.g., description doesn't matter)
      };
    },
    (data) => { },
    { delay: 750 }
  ); //debounce typing into name/description etc fields

  dispose() {
    this.saveProjectDisposer();
  }

  checkProjUpdate = (id, currentHash, enqueueSnackbar) => {
    return checkHash(id, currentHash, enqueueSnackbar);
  };

  rebaseline = (period, baselineDescription, baselined, callback) => {
    console.log(baselineDescription);
    rebaselineProject(this, period, baselineDescription, (returedProject) => {
      let project = ProjectStore.fromJS(returedProject);
      this.currentHash = project.currentHash;
      this.baselines = project.baselines;
      this.rootTaskPackage = project.rootTaskPackage;
      baselined(true);

      callback();
    });
  };

  updateProjectInfo = (file, enqueueSnackbar) => {
    getProjectInfo(file, enqueueSnackbar, (returedProject) => {
      let project_U = ProjectStore.fromJS(returedProject);
      this.rootTaskPackage = project_U.rootTaskPackage;
    });
  };

  updatePrebaselinePeriods = (loadedPrebaseline, callback) => {
    this.preBaselines = [];
    this.getAllPrebaselines(loadedPrebaseline);
  };

  getAllPrebaselines = (loadedPrebaseline) => {
    projectPrebaselines(this, loadedPrebaseline, (returnedProjPrebaseline) => {
      returnedProjPrebaseline.forEach((preBaseline) => {
        this.preBaselines.push(PreBaseline.fromJS(preBaseline));
      });
    });
  };

  getUnarcPrebaselinePeriods = (loadedPrebaseline) => {
    if (this.preBaselines === undefined || this.preBaselines === null) {
      this.preBaselines = [];
      this.getAllPrebaselines(loadedPrebaseline);
    }

    return this.preBaselines
      .filter((x) => x.isArchive === false)
      .map((formatedPreBaseline) => {
        return formatedPreBaseline.preBaselinePeriod;
      });
  };

  toJS() {
    let plainObject = {};
    plainObject.name = this.name;
    plainObject.currentHash = this.currentHash;
    plainObject.id = this.id;
    plainObject.ownerName = this.ownerName;
    plainObject.folderName = this.folderName;
    plainObject.baselines = this.baselines.map((baseline) => baseline.toJS());
    plainObject.rootTaskPackage =
      this.rootTaskPackage.toJS != null ? this.rootTaskPackage.toJS() : null;
    plainObject.rootTaskPackageId = this.rootTaskPackageId;
    plainObject.isArchive = this.isArchive;
    plainObject.dataPeriod = this.dataPeriod;
    plainObject.periodToDateLookup = this.periodToDateLookup;
    return plainObject;
  }

  static fromJS(savedState) {
    const projectStore = new ProjectStore();
    if (savedState != null) {
      projectStore.name = savedState.name;
      projectStore.currentHash = savedState.currentHash;
      projectStore.id = savedState.id;
      projectStore.ownerName = savedState.ownerName;
      projectStore.folderName = savedState.folderName;
      projectStore.baselines =
        savedState.baselines === undefined || savedState.baselines === null
          ? []
          : savedState.baselines.map((baseline) => NBaseline.fromJS(baseline));
      projectStore.rootTaskPackage = TaskPackage.fromJS(
        projectStore,
        savedState.rootTaskPackage,
        ""
      );
      projectStore.rootTaskPackageId = savedState.rootTaskPackageId;
      projectStore.isArchive = savedState.isArchive; 
      projectStore.dataPeriod = savedState.dataPeriod;
      projectStore.periodToDateLookup = savedState.periodToDateLookup;
    }
    return projectStore;
  }
}

const ProjectStoreContext = createContext(new ProjectStore());

export const ProjectStoreProvider = ({ children, store }) => {
  return (
    <ProjectStoreContext.Provider value={observable(store)}>
      {children}
    </ProjectStoreContext.Provider>
  );
};

/* Hook to use store in any functional component */
export const useProjectStore = () => useContext(ProjectStoreContext);

/* HOC to inject store to any functional or class component */
export const withProjectStore = (Component) => (props) => {
  return <Component {...props} store={useProjectStore()} />;
};
